'use client';

import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import {
  useTelegramGeoGetCities,
  useTelegramGeoGetCountries,
  useTelegramGeoGetRegions,
  useTelegramGeoSearch,
} from '@/generated';
import { useDebouncedValue } from '@/hooks/useDebouncedValue.hook';
import { IconArrowLeft, IconCheck, IconChevronRight } from '@tabler/icons-react';
import { TextInput } from '@/components/ui/text-input';
import { useLocale, useTranslations } from 'next-intl';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import clsx from 'clsx';
import { getGeoSelectionPlacementLabel } from '@/utils/getProductPlacementLabel';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

// ----- Types -----

export type LocationSelectorResponse = {
  regionId?: string;
  cityId?: string;
  districtId?: string;
  countryId?: string;
  longitude: number;
  latitude: number;
};

export type ResolvedGeoLabels = {
  cityName?: string | null;
  regionName?: string | null;
  countryName?: string | null;
};

type LocationSelectorBaseProps = {
  defaultState?: Omit<LocationSelectorResponse, 'longitude' | 'latitude'>;
  resolvedGeoLabels?: ResolvedGeoLabels | null;
  placeholder?: string;
  allowPartSelection?: boolean;
  allowRegionSelection?: boolean;
  allowCountrySelection?: boolean;
  hideCountryOnlyPlacement?: boolean;
  onChange: (response: LocationSelectorResponse) => void;
};

type LocationSelectorDialogProps = LocationSelectorBaseProps & {
  GeoShowElement: (props: { text: string; rightSideText?: boolean }) => React.ReactNode;
  rightSideText?: boolean;
};

type State = 'country' | 'region' | 'city';

type ListItemConfig = {
  key: string;
  value: string;
  name: string;
  subtitle?: string;
  hasInner?: boolean;
  isCurrent?: boolean;
  onClick: (value: string) => void;
};

export type LocationSelectorState = {
  filterQuery: string;
  setFilterQuery: (val: string) => void;
  currentScene: State;
  title: string;
  searchLabel: string;
  items: ListItemConfig[];
  isGeoPending: boolean;
  currentPlacement: string;
  displayPlacement: string;
  handleBackClick: () => void;
  syncFromDefaultState: () => void;
  resetToDefaults: () => void;
};

const geoI18nQueryKeys = {
  countries: (locale: string) => ['geo-i18n', 'countries', locale] as const,
  regions: (countryId: string, locale: string) => ['geo-i18n', 'regions', countryId, locale] as const,
  cities: (regionId: string, locale: string) => ['geo-i18n', 'cities', regionId, locale] as const,
  search: (countryId: string, locale: string, query: string) =>
    ['geo-i18n', 'search', countryId, locale, query] as const,
};

export function useLocationSelectorState({
  defaultState,
  resolvedGeoLabels,
  onChange,
  placeholder,
  allowPartSelection = true,
  allowRegionSelection,
  allowCountrySelection,
  hideCountryOnlyPlacement = false,
  onRequestClose,
}: LocationSelectorBaseProps & { onRequestClose?: () => void }): LocationSelectorState {
  const canSelectRegionLevel = allowRegionSelection ?? allowPartSelection;
  const canSelectCountryLevel = allowCountrySelection ?? allowPartSelection;

  const t = useTranslations('locationSelector');
  const locale = useLocale();
  const finalPlaceholder = placeholder ?? t('placeholder');

  const [filterQuery, setFilterQuery] = useState('');
  const [currentScene, setCurrentScene] = useState<State>(() =>
    defaultState?.cityId || defaultState?.regionId || defaultState?.countryId ? 'region' : 'country',
  );

  const [countryId, setCountryId] = useState(defaultState?.countryId ?? '');
  const [regionId, setRegionId] = useState(defaultState?.regionId ?? '');
  const [cityId, setCityId] = useState(defaultState?.cityId ?? '');
  const trimmedFilterQuery = filterQuery.trim();
  const debouncedFilterQuery = useDebouncedValue(trimmedFilterQuery, 300);
  const isRegionSearchActive = currentScene === 'region' && countryId.length > 0 && debouncedFilterQuery.length >= 2;

  const { data: countriesResponse = [], isFetching: isCountriesPending } = useTelegramGeoGetCountries({
    query: { queryKey: geoI18nQueryKeys.countries(locale) },
  });
  const { data: regionsResponse = [], isFetching: isRegionsPending } = useTelegramGeoGetRegions(countryId, {
    query: {
      enabled: countryId.length > 0,
      queryKey: geoI18nQueryKeys.regions(countryId, locale),
    },
  });
  const { data: citiesResponse = [], isFetching: isCitiesPending } = useTelegramGeoGetCities(regionId, {
    query: {
      enabled: regionId.length > 0,
      queryKey: geoI18nQueryKeys.cities(regionId, locale),
    },
  });
  const { data: geoSearchResponse = [], isFetching: isGeoSearchPending } = useTelegramGeoSearch(
    {
      countryId,
      query: debouncedFilterQuery,
      take: 20,
    },
    {
      query: {
        enabled: isRegionSearchActive,
        queryKey: geoI18nQueryKeys.search(countryId, locale, debouncedFilterQuery),
      },
    },
  );

  const stateMapped: Record<State, string> = {
    region: t('region'),
    city: t('city'),
    country: t('country'),
  };

  const filterSearch = ({ name }: { name: string }) => name?.toLowerCase()?.includes(trimmedFilterQuery.toLowerCase());

  const regions = regionsResponse.filter(filterSearch);
  const cities = citiesResponse.filter(filterSearch);
  const countries = countriesResponse.filter(filterSearch);

  const currentPlacement = useMemo(() => {
    if (hideCountryOnlyPlacement && countryId.length > 0 && !regionId.length && !cityId.length) {
      return finalPlaceholder;
    }

    const label = getGeoSelectionPlacementLabel({
      cityId,
      regionId,
      countryId,
      cityName: cityId ? cities.find((x) => x.id === cityId)?.name || resolvedGeoLabels?.cityName : undefined,
      regionName: regionId ? regions.find((x) => x.id === regionId)?.name || resolvedGeoLabels?.regionName : undefined,
      countryName: countryId
        ? countries.find((x) => x.id === countryId)?.name || resolvedGeoLabels?.countryName
        : undefined,
      locale,
    });

    return label || finalPlaceholder;
  }, [
    regionId,
    cityId,
    finalPlaceholder,
    regions,
    cities,
    countries,
    countryId,
    hideCountryOnlyPlacement,
    resolvedGeoLabels,
    locale,
  ]);

  const [lastValidPlacement, setLastValidPlacement] = useState(finalPlaceholder);

  useEffect(() => {
    if (currentPlacement !== finalPlaceholder) {
      setLastValidPlacement(currentPlacement);
    }
  }, [currentPlacement, finalPlaceholder]);

  const displayPlacement =
    currentPlacement !== finalPlaceholder
      ? currentPlacement
      : lastValidPlacement !== finalPlaceholder
        ? lastValidPlacement
        : currentPlacement;

  const defaultCountryId = defaultState?.countryId ?? '';
  const defaultRegionId = defaultState?.regionId ?? '';
  const defaultCityId = defaultState?.cityId ?? '';

  const syncFromDefaultState = useCallback(() => {
    setCurrentScene(defaultCityId || defaultRegionId || defaultCountryId ? 'region' : 'country');
    setRegionId(defaultRegionId);
    setCityId(defaultCityId);
    setCountryId(defaultCountryId);
    setFilterQuery('');
  }, [defaultCityId, defaultCountryId, defaultRegionId]);

  useEffect(() => {
    setRegionId(defaultRegionId);
    setCityId(defaultCityId);
    setCountryId(defaultCountryId);

    if (!defaultCityId && !defaultRegionId && !defaultCountryId) {
      setCurrentScene('country');
    }
  }, [defaultCountryId, defaultRegionId, defaultCityId]);

  useEffect(() => {
    setFilterQuery('');
  }, [currentScene]);

  const items = useMemo(() => {
    switch (currentScene) {
      case 'country': {
        const response: ListItemConfig[] = [];

        response.push(
          ...countries.map((x) => ({
            key: x.id,
            value: x.id,
            name: x.name,
            hasInner: true,
            isCurrent: x.id === countryId,
            onClick: (value: string) => {
              setCountryId(value);
              setCurrentScene('region');
            },
          })),
        );

        return response;
      }

      case 'region': {
        if (isRegionSearchActive) {
          const response: ListItemConfig[] = [];

          geoSearchResponse.forEach((item) => {
            if (item.city) {
              const city = item.city;
              const nextRegionId = item.region?.id ?? city.regionId;

              response.push({
                key: `city-${city.id}`,
                value: city.id,
                name: city.name,
                subtitle: item.region?.name,
                isCurrent: city.id === cityId,
                onClick: () => {
                  setRegionId(nextRegionId);
                  setCityId(city.id);
                  onChange({
                    countryId,
                    regionId: nextRegionId,
                    cityId: city.id,
                    districtId: undefined,
                    longitude: city.longitude,
                    latitude: city.latitude,
                  });
                  setCurrentScene('region');
                  onRequestClose?.();
                },
              });
              return;
            }

            if (item.region) {
              response.push({
                key: `region-${item.region.id}`,
                value: item.region.id,
                name: item.region.name,
                hasInner: true,
                isCurrent: item.region.id === regionId,
                onClick: (value: string) => {
                  setRegionId(value);
                  setCurrentScene('city');
                },
              });
            }
          });

          return response;
        }

        const response: ListItemConfig[] = [];

        if (canSelectCountryLevel) {
          response.push({
            key: 'all',
            value: '',
            name: t('allRegions'),
            hasInner: false,
            onClick: () => {
              const country = countries.find(({ id }) => id === countryId);
              setRegionId('');
              onChange({
                countryId: countryId,
                regionId: undefined,
                cityId: undefined,
                districtId: undefined,
                longitude: country?.longitude || 0,
                latitude: country?.latitude || 0,
              });
              setCurrentScene('region');
              onRequestClose?.();
            },
          });
        }

        response.push(
          ...regions.map((x) => ({
            key: x.id,
            isCurrent: x.id === regionId,
            value: x.id,
            name: x.name,
            hasInner: true,
            onClick: (value: string) => {
              setRegionId(value);
              setCurrentScene('city');
            },
          })),
        );
        return response;
      }
      case 'city': {
        const response: ListItemConfig[] = [];

        if (canSelectRegionLevel) {
          response.push({
            key: 'all',
            value: '',
            name: t('allCities'),
            hasInner: false,
            onClick: () => {
              const country = countries.find(({ id }) => id === countryId);
              setCityId('');
              onChange({
                countryId: countryId,
                regionId,
                cityId: undefined,
                districtId: undefined,
                longitude: country?.longitude || 0,
                latitude: country?.latitude || 0,
              });
              setCurrentScene('region');
              onRequestClose?.();
            },
          });
        }

        response.push(
          ...cities.map((x) => ({
            key: x.id,
            value: x.id,
            name: x.name,
            hasInner: x.hasDistricts,
            isCurrent: x.id === cityId,
            onClick: () => {
              setCityId(x.id);
              onChange({
                countryId: countryId,
                regionId,
                cityId: x.id,
                longitude: x.longitude,
                latitude: x.latitude,
              });
              setCurrentScene('region');
              onRequestClose?.();
            },
          })),
        );
        return response;
      }
    }
  }, [
    currentScene,
    regions,
    cityId,
    onChange,
    regionId,
    t,
    cities,
    countries,
    countryId,
    canSelectCountryLevel,
    canSelectRegionLevel,
    onRequestClose,
    geoSearchResponse,
    isRegionSearchActive,
  ]);

  const resetToDefaults = useCallback(() => {
    syncFromDefaultState();
  }, [syncFromDefaultState]);

  const handleBackClick = useCallback(() => {
    if (currentScene === 'country') {
      resetToDefaults();
      onRequestClose?.();
    } else if (currentScene === 'region') {
      setCurrentScene('country');
      setRegionId('');
    } else if (currentScene === 'city') {
      setCurrentScene('region');
      setCityId('');
    }
  }, [currentScene, resetToDefaults, onRequestClose]);

  const isGeoPending =
    isCountriesPending || isRegionsPending || isCitiesPending || (isRegionSearchActive && isGeoSearchPending);

  return {
    filterQuery,
    setFilterQuery,
    currentScene,
    title: t('selectTitle', {
      locationType: currentScene === 'region' ? t('regionAndCity') : stateMapped[currentScene],
    }),
    searchLabel: t('searchLabel'),
    items,
    isGeoPending,
    currentPlacement,
    displayPlacement,
    handleBackClick,
    syncFromDefaultState,
    resetToDefaults,
  };
}

// ----- Presentational content (modal-agnostic) -----

export function LocationSelectorContent({
  state,
  showBackButton = true,
  fillHeight = false,
  className,
}: {
  state: LocationSelectorState;
  onClose?: () => void;
  showBackButton?: boolean;
  fillHeight?: boolean;
  className?: string;
}) {
  return (
    <div className={clsx('relative flex flex-col gap-3', fillHeight && 'min-h-0 flex-1', className)}>
      <div className='flex shrink-0 flex-col gap-3'>
        <DialogTitle className='text-[22px] font-semibold leading-[26px]'>{state.title}</DialogTitle>
        <TextInput
          autoFocus
          value={state.filterQuery}
          onChange={(e) => state.setFilterQuery(e.target.value)}
          label={state.searchLabel}
        />
      </div>

      <ScrollArea
        className={clsx(
          '-mx-2 [&>[data-slot=scroll-area-viewport]>div]:block!',
          fillHeight ? 'min-h-0 flex-1' : 'min-h-[250px] [&>[data-slot=scroll-area-viewport]]:max-h-[400px]',
        )}
      >
        <div className='flex flex-col gap-0.5 px-2 pb-2'>
          {state.items.map((item) => (
            <ListItem
              key={item.key}
              value={item.value}
              name={item.name}
              subtitle={item.subtitle}
              hasInner={item.hasInner}
              isCurrent={item.isCurrent}
              onClick={item.onClick}
            />
          ))}
        </div>
      </ScrollArea>

      {showBackButton && <LocationBackButton onClick={state.handleBackClick} className='top-4 start-4' />}
    </div>
  );
}

function LocationBackButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={clsx(
        'absolute z-10 flex items-center justify-center opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden [&_svg]:pointer-events-none [&_svg]:size-5',
        className,
      )}
    >
      <IconArrowLeft className='rtl:rotate-180' />
    </button>
  );
}

// ----- Default Radix Dialog wrapper (existing behavior) -----

export function LocationSelector({
  defaultState,
  resolvedGeoLabels,
  onChange,
  placeholder,
  GeoShowElement,
  allowPartSelection = true,
  allowRegionSelection,
  allowCountrySelection,
  hideCountryOnlyPlacement = false,
  rightSideText = false,
}: LocationSelectorDialogProps) {
  const [isOpen, setIsOpen] = useState(false);

  const state = useLocationSelectorState({
    defaultState,
    resolvedGeoLabels,
    onChange,
    placeholder,
    allowPartSelection,
    allowRegionSelection,
    allowCountrySelection,
    hideCountryOnlyPlacement,
    onRequestClose: () => setIsOpen(false),
  });

  const onChangeOpened = useCallback(
    (newVal: boolean) => {
      if (newVal) {
        startTransition(() => {
          state.syncFromDefaultState();
          setIsOpen(true);
        });
        return;
      }
      state.resetToDefaults();
      setIsOpen(false);
    },
    [state],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onChangeOpened}>
      <DialogTrigger asChild>
        <GeoShowElement text={state.displayPlacement} rightSideText={rightSideText} />
      </DialogTrigger>
      <DialogContent className='z-163 flex h-[min(85vh,680px)] w-[90vw] max-w-[500px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[500px]'>
        <LocationBackButton onClick={state.handleBackClick} className='top-4 start-4' />
        <LocationSelectorContent state={state} showBackButton={false} fillHeight className='px-5 pt-12' />
      </DialogContent>
    </Dialog>
  );
}

// ----- Embedded/controlled version for host modals -----

export function LocationSelectorEmbedded({
  showBackButton = true,
  onClose,
  className,
  ...baseProps
}: LocationSelectorBaseProps & { showBackButton?: boolean; onClose?: () => void; className?: string }) {
  const state = useLocationSelectorState({ ...baseProps, onRequestClose: onClose });
  return <LocationSelectorContent state={state} showBackButton={showBackButton} className={className} />;
}

// ----- List item -----

type ListItemProps = {
  value: string;
  name: string;
  subtitle?: string;
  onClick: (value: string) => void;
  hasInner?: boolean;
  isCurrent?: boolean;
};

function ListItem({ value, name, subtitle, onClick, hasInner = false, isCurrent = false }: ListItemProps) {
  return (
    <button
      type='button'
      onClick={() => onClick(value)}
      className={clsx(
        'flex w-full items-center justify-between gap-3 rounded-[12px] px-3 text-start transition-colors hover:bg-brand-muted focus-visible:bg-brand-muted focus-visible:outline-hidden',
        subtitle ? 'min-h-[56px] py-2' : 'min-h-[48px] py-2.5',
        isCurrent && 'bg-brand-muted font-medium',
      )}
    >
      <div className='min-w-0'>
        <div className='truncate text-[16px] leading-[20px]'>{name}</div>
        {subtitle && (
          <div className='mt-0.5 truncate text-[13px] leading-[16px] text-brand-muted-foreground'>{subtitle}</div>
        )}
      </div>
      <div className='flex shrink-0 items-center gap-1'>
        {isCurrent && <IconCheck size={20} className='text-brand-primary' />}
        {hasInner && <IconChevronRight size={20} className='text-brand-muted-foreground rtl:rotate-180' />}
      </div>
    </button>
  );
}
